import React, { useState } from 'react';
import { Search, Mail, Phone } from 'lucide-react';
import { usePageTitle, Avatar } from '../components/AppShell';
import { useLang } from '../lib/lang';
import { type AuthUser } from '../lib/auth';
import { T } from '../lib/tokens';

interface Props { user: AuthUser }

interface StaffEntry {
  id:          string;
  name:        string;
  roleMs:      string;
  roleEn:      string;
  department:  string;
  departmentEn:string;
  email:       string;
  phone?:      string;
  isClassTeacher?: boolean;
}

const STAFF: StaffEntry[] = [
  // Class teacher — always shown first as hero card
  { id:'ct1', name:'Cikgu Aishah Hj Salleh', roleMs:'Guru Kelas — Tingkatan 4A', roleEn:'Class Teacher — Form 4A', department:'Perguruan', departmentEn:'Teaching', email:'aishah.salleh@smas-ali.edu.bn', phone:'+673-2229500 (Ext 214)', isClassTeacher:true },

  // Admin
  { id:'a1', name:'Hj Karim bin Hj Othman', roleMs:'Pengetua', roleEn:'Principal', department:'Pentadbiran', departmentEn:'Administration', email:'pengetua@smas-ali.edu.bn', phone:'+673-2229500 (Ext 101)' },
  { id:'a2', name:'Pn Norzaimah Hj Ahmad',  roleMs:'Penolong Kanan', roleEn:'Vice Principal', department:'Pentadbiran', departmentEn:'Administration', email:'pk@smas-ali.edu.bn', phone:'+673-2229500 (Ext 102)' },
  { id:'a3', name:'En Yazid Hj Sulaiman',   roleMs:'Pegawai Am', roleEn:'Admin Officer', department:'Pentadbiran', departmentEn:'Administration', email:'am@smas-ali.edu.bn', phone:'+673-2229500' },

  // Subject teachers for Ahmad's classes
  { id:'t1', name:'Cikgu Ahmad Zulkarnain', roleMs:'Guru Matematik', roleEn:'Mathematics Teacher', department:'Perguruan', departmentEn:'Teaching', email:'ahmad.zulkarnain@smas-ali.edu.bn' },
  { id:'t2', name:'Cikgu Rashid Osman',      roleMs:'Guru Bahasa Inggeris', roleEn:'English Teacher', department:'Perguruan', departmentEn:'Teaching', email:'rashid.osman@smas-ali.edu.bn' },
  { id:'t3', name:'Cikgu Nurul Huda',         roleMs:'Guru Sains', roleEn:'Science Teacher', department:'Perguruan', departmentEn:'Teaching', email:'nurul.huda@smas-ali.edu.bn' },
  { id:'t4', name:'Cikgu Hj Masri',           roleMs:'Guru Sejarah', roleEn:'History Teacher', department:'Perguruan', departmentEn:'Teaching', email:'masri@smas-ali.edu.bn' },
  { id:'t5', name:'Cikgu Siti Noraini',        roleMs:'Guru Geografi', roleEn:'Geography Teacher', department:'Perguruan', departmentEn:'Teaching', email:'siti.noraini@smas-ali.edu.bn' },
  { id:'t6', name:'Ustaz Hafiz Jamil',         roleMs:'Guru Pendidikan Islam', roleEn:'Islamic Studies Teacher', department:'Perguruan', departmentEn:'Teaching', email:'hafiz.jamil@smas-ali.edu.bn' },
  { id:'t7', name:'Cikgu Azlan Hj Nordin',    roleMs:'Guru Pendidikan Jasmani', roleEn:'P.E. Teacher', department:'Perguruan', departmentEn:'Teaching', email:'azlan.nordin@smas-ali.edu.bn' },
];

export function ParentDirektoriPage({ user }: Props) {
  const { lang } = useLang();
  const E = lang === 'en';
  usePageTitle(E ? 'School Directory' : 'Direktori Sekolah');

  const [query, setQuery] = useState('');

  const classTeacher = STAFF.find(s => s.isClassTeacher)!;
  const filteredStaff = STAFF.filter(s => !s.isClassTeacher && (
    !query.trim() ||
    s.name.toLowerCase().includes(query.toLowerCase()) ||
    (E ? s.roleEn : s.roleMs).toLowerCase().includes(query.toLowerCase())
  ));

  const admin   = filteredStaff.filter(s => s.department === 'Pentadbiran');
  const teachers = filteredStaff.filter(s => s.department === 'Perguruan');

  return (
    <div style={{ maxWidth: '860px' }}>
      <div className="page-header">
        <div>
          <h1 style={{ fontFamily: T.font.serif, fontSize: '24px', fontWeight: 700, color: T.color.textPrimary, marginBottom: '4px' }}>
            {E ? 'School Directory' : 'Direktori Sekolah'}
          </h1>
          <p style={{ fontSize: '13px', color: T.color.textSecondary }}>
            SMAS Sayyidina Ali · {E ? 'Contact directory for school personnel' : 'Direktori hubungan kakitangan sekolah'}
          </p>
        </div>
      </div>

      {/* Class teacher hero card */}
      <div style={{
        background: '#FFFFFF', border: '1px solid #E5E7EB',
        borderTop: '4px solid #F7D000',
        borderRadius: '10px', padding: '20px 24px',
        marginBottom: '24px',
        display: 'flex', alignItems: 'center', gap: '20px',
      }}>
        <Avatar email={classTeacher.email} size={52} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '10px', fontWeight: 700, color: '#F7D000', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '4px' }}>
            {E ? 'Your Child\'s Class Teacher' : 'Guru Kelas Anak Anda'}
          </div>
          <div style={{ fontFamily: T.font.serif, fontSize: '18px', fontWeight: 700, color: T.color.textPrimary, marginBottom: '2px' }}>
            {classTeacher.name}
          </div>
          <div style={{ fontSize: '13px', color: T.color.textSecondary, marginBottom: '10px' }}>
            {E ? classTeacher.roleEn : classTeacher.roleMs}
          </div>
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            <a href={`mailto:${classTeacher.email}`} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#1D4ED8', textDecoration: 'none' }}>
              <Mail size={14} /> {classTeacher.email}
            </a>
            {classTeacher.phone && (
              <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: T.color.textSecondary }}>
                <Phone size={14} /> {classTeacher.phone}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Search */}
      <div style={{ position: 'relative', marginBottom: '20px' }}>
        <Search size={15} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF', pointerEvents: 'none' }} />
        <input
          type="text"
          className="form-input"
          placeholder={E ? 'Search by name or role…' : 'Cari nama atau jawatan…'}
          value={query}
          onChange={e => setQuery(e.target.value)}
          style={{ paddingLeft: '36px' }}
        />
      </div>

      {/* Admin section */}
      {admin.length > 0 && (
        <div style={{ marginBottom: '24px' }}>
          <h3 style={{ fontSize: '11px', fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '12px' }}>
            {E ? 'Administration' : 'Pentadbiran'}
          </h3>
          <div style={{ background: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: '10px', overflow: 'hidden' }}>
            {admin.map((s, i) => (
              <StaffRow key={s.id} staff={s} E={E} isLast={i === admin.length - 1} />
            ))}
          </div>
        </div>
      )}

      {/* Teachers section */}
      {teachers.length > 0 && (
        <div>
          <h3 style={{ fontSize: '11px', fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '12px' }}>
            {E ? 'Subject Teachers (your child\'s classes)' : 'Guru Matapelajaran (kelas anak anda)'}
          </h3>
          <div style={{ background: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: '10px', overflow: 'hidden' }}>
            {teachers.map((s, i) => (
              <StaffRow key={s.id} staff={s} E={E} isLast={i === teachers.length - 1} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function StaffRow({ staff, E, isLast }: { staff: StaffEntry; E: boolean; isLast: boolean }) {
  return (
    <div
      className="tbl-row"
      style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '14px 20px', borderBottom: isLast ? 'none' : '1px solid #F3F4F6' }}
    >
      <Avatar email={staff.email} size={36} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '13px', fontWeight: 600, color: T.color.textPrimary }}>{staff.name}</div>
        <div style={{ fontSize: '12px', color: T.color.textSecondary }}>{E ? staff.roleEn : staff.roleMs}</div>
      </div>
      <a href={`mailto:${staff.email}`} style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', color: '#1D4ED8', textDecoration: 'none', flexShrink: 0 }}>
        <Mail size={13} /> {staff.email}
      </a>
    </div>
  );
}
